import type { AspectRatioProps, MotionPropsFor } from '@hover/blueprint';
import {
  AspectRatio,
  Body,
  Box,
  Heading,
  Image,
  Motion,
  Stack,
  VStack,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import { iconPlus } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect } from 'react';
import { Trans } from 'react-i18next';

import ExteriorOne from 'src/assets/img/homeownerApp/projects/ideas/exterior-1.png';
import ExteriorTwo from 'src/assets/img/homeownerApp/projects/ideas/exterior-2.png';
import ExteriorThree from 'src/assets/img/homeownerApp/projects/ideas/exterior-3.png';
import ExteriorFour from 'src/assets/img/homeownerApp/projects/ideas/exterior-4.png';
import { LineBreak } from 'src/modules/designOrders/components';
import { PROJECT_DETAILS_PADDING } from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Button } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

const EXTERIOR_IMAGES: Record<string, string> = {
  ExteriorOne,
  ExteriorTwo,
  ExteriorThree,
  ExteriorFour,
};

const MotionAspectRatio = motion(AspectRatio);

const ImageThumbnails = (props: MotionPropsFor<AspectRatioProps>) => (
  <MotionAspectRatio
    borderRadius={400}
    mt="0 !important"
    overflow="hidden"
    position="absolute"
    ratio={1}
    shadow="default"
    transition={{
      duration: 0.65,
    }}
    {...props}
  />
);

export const EmptyIdeas = () => {
  const [images, setImages] = React.useState(Object.keys(EXTERIOR_IMAGES));
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { setCurrentMenu } = useHomeownerAppContext();
  const onAddPhotoClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.UPLOAD_PHOTO);
  };
  const imageWidth = useBreakpointValue({ base: '180px', tablet: '287px' });
  const leftImage = images[0];
  const centerImage = images[1];
  const rightImage = images[2];

  useEffect(() => {
    const timer = setInterval(() => {
      setImages(prevImages => {
        const [first, ...rest] = prevImages;
        return [...rest, first];
      });
    }, 2500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Stack
      backgroundColor="neutral.50"
      borderRadius={400}
      data-testid="empty-ideas"
      flexDirection={{ base: 'column', desktop: 'row' }}
      mx={PROJECT_DETAILS_PADDING}
      py={{ base: 600, tablet: 700, desktop: 900 }}
    >
      <Stack
        alignItems="center"
        flex={{ desktop: 1 }}
        height={imageWidth}
        justifyContent="center"
        pb={{ base: 600, tablet: 700, desktop: 900 }}
        position="relative"
        px={{ base: 600, tablet: 700, desktop: 900 }}
      >
        <Motion.Soft>
          <AnimatePresence mode="popLayout">
            <ImageThumbnails
              animate={{ x: '-50%', scale: '65%' }}
              exit={{ x: '0%', scale: '65%' }}
              initial={{ x: '0%', scale: '100%' }}
              key={leftImage}
              width={imageWidth}
            >
              <Image alt="left-image" src={EXTERIOR_IMAGES[leftImage]} />
            </ImageThumbnails>
            <ImageThumbnails
              animate={{ x: '0%', scale: '100%', zIndex: 2 }}
              exit={{ x: '-50%', scale: '65%', zIndex: 1 }}
              initial={{ x: '0%', scale: '100%', zIndex: 2 }}
              key={centerImage}
              width={imageWidth}
            >
              <Image
                alt="center-image"
                position="absolute"
                src={EXTERIOR_IMAGES[centerImage]}
              />
            </ImageThumbnails>
            <ImageThumbnails
              animate={{ x: '50%', scale: '65%', zIndex: 1 }}
              exit={{ x: '0%', scale: '100%', zIndex: 2 }}
              initial={{ x: '0%', scale: '65%', zIndex: 1 }}
              key={rightImage}
              width={imageWidth}
            >
              <Image alt="right-image" src={EXTERIOR_IMAGES[rightImage]} />
            </ImageThumbnails>
          </AnimatePresence>
        </Motion.Soft>
      </Stack>
      <VStack
        alignItems="center"
        flex={{ desktop: 1 }}
        justifyContent="center"
        textAlign="center"
      >
        <Heading
          mb={{ base: 300, tablet: 400 }}
          textStyle={{
            base: 'heading500WithCapsize',
            tablet: 'heading600WithCapsize',
          }}
        >
          {t('projects.ideas.ideasForYou.title')}
        </Heading>
        <Body
          color="text.secondary"
          mt="0 !important"
          textStyle={{ base: 'bodySmall', tablet: 'bodyMedium' }}
        >
          <Trans
            components={{ LineBreak: <LineBreak /> }}
            i18nKey="projects.ideas.ideasForYou.subtitle"
            t={t}
          />
        </Body>
        <Box paddingTop={{ base: 500, desktop: 600 }}>
          <Button iconAfter={iconPlus} onClick={onAddPhotoClick} size="large">
            {t('projects.ideas.ideasForYou.cta')}
          </Button>
        </Box>
      </VStack>
    </Stack>
  );
};
